import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { inputCls } from "../site/formPrimitives";
import { Plus, Trash2, Tag, ChevronUp, ChevronDown, Search } from "lucide-react";
import { filterModels } from "../site/ModelPicker";

export default function ModelsAdmin() {
  const [categories, setCategories] = useState([]);
  const [problems, setProblems] = useState([]);
  const [catId, setCatId] = useState(null);
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(null);
  const [prices, setPrices] = useState({});
  const [newModel, setNewModel] = useState({ brand: "", name: "", group: "" });
  const [search, setSearch] = useState("");
  const [newCat, setNewCat] = useState("");

  const loadCats = () => api.get("/content/categories").then((r) => { setCategories(r.data); if (!catId && r.data[0]) setCatId(r.data[0].id); });
  useEffect(() => { loadCats(); api.get("/content/problems").then((r) => setProblems(r.data)); }, []);
  useEffect(() => { if (catId) { api.get(`/content/models?category_id=${catId}`).then((r) => setModels(r.data)); setModelId(null); } }, [catId]);
  useEffect(() => {
    if (modelId) api.get(`/content/prices?model_id=${modelId}`).then((r) => {
      const m = {}; r.data.forEach((p) => { m[p.problem] = p.price; }); setPrices(m);
    });
  }, [modelId]);

  const addCat = async () => {
    if (!newCat.trim()) return;
    await api.post("/admin/categories", { name: newCat, order: categories.length + 1 });
    setNewCat(""); toast.success("Kategori lagt til"); loadCats();
  };
  const delCat = async (id) => {
    if (!window.confirm("Slette kategori og alle modeller under den?")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Slettet");
    if (catId === id) setCatId(null); loadCats();
  };

  const addModel = async () => {
    if (!newModel.name.trim()) { toast.error("Modellnavn kreves"); return; }
    await api.post("/admin/models", { category_id: catId, brand: newModel.brand, name: newModel.name, group: newModel.group });
    setNewModel({ brand: "", name: "", group: "" }); toast.success("Modell lagt til");
    api.get(`/content/models?category_id=${catId}`).then((r) => setModels(r.data));
  };
  const delModel = async (id) => {
    if (!window.confirm("Slette modellen og prisene?")) return;
    await api.delete(`/admin/models/${id}`); toast.success("Slettet");
    if (modelId === id) setModelId(null);
    api.get(`/content/models?category_id=${catId}`).then((r) => setModels(r.data));
  };

  const moveModel = async (id, direction) => {
    await api.put(`/admin/models/${id}/move`, { direction });
    const r = await api.get(`/content/models?category_id=${catId}`);
    setModels(r.data);
  };

  const savePrice = async (problem, value) => {
    const price = value === "" ? null : Number(value);
    await api.put("/admin/prices", { model_id: modelId, problem, price });
    setPrices((p) => ({ ...p, [problem]: price }));
    toast.success("Pris lagret");
  };

  const activeModel = models.find((m) => m.id === modelId);
  const visible = filterModels(models, search);

  return (
    <div data-testid="admin-models">
      <h1 className="font-heading text-2xl font-bold text-navy">Modeller og priser</h1>
      <p className="mt-1 text-sm text-ink/60">La stå tomt for «Pris etter diagnostikk». Alle priser er i NOK.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Categories */}
        <div className="rounded-xl border border-hairline bg-white p-4 shadow-sm">
          <h3 className="font-heading font-bold text-navy mb-3">Kategorier</h3>
          <div className="space-y-1.5">
            {categories.map((c) => (
              <div key={c.id} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer ${catId === c.id ? "bg-electric text-white" : "hover:bg-slate-50 text-navy"}`} onClick={() => setCatId(c.id)} data-testid={`cat-${c.name}`}>
                <span className="font-medium">{c.name}</span>
                <button onClick={(e) => { e.stopPropagation(); delCat(c.id); }} className={catId === c.id ? "text-white/80" : "text-ink/40 hover:text-red-600"}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input className={inputCls} placeholder="Ny kategori" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="cat-input" />
            <button onClick={addCat} className="shrink-0 rounded-lg bg-navy px-3 text-white" data-testid="cat-add"><Plus className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Models */}
        <div className="rounded-xl border border-hairline bg-white p-4 shadow-sm">
          <h3 className="font-heading font-bold text-navy mb-3">Modeller <span className="text-xs font-normal text-ink/50">({models.length})</span></h3>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" />
            <input className={`${inputCls} pl-8 py-2 text-sm`} placeholder="Søk modell, A-nummer, år ..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="model-search" />
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {visible.length === 0 && models.length > 0 && <p className="text-xs text-ink/40 px-1">Ingen treff.</p>}
            {visible.map((m, mi) => (
              <React.Fragment key={m.id}>
              {m.group && (mi === 0 || visible[mi - 1].group !== m.group) && <div className="px-1 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink/45">{m.group}</div>}
              <div className={`flex items-center justify-between gap-1 rounded-lg px-3 py-2 text-sm cursor-pointer ${modelId === m.id ? "bg-navy text-white" : "hover:bg-slate-50 text-navy"}`} onClick={() => setModelId(m.id)} data-testid={`model-${m.name}`}>
                <span className="font-medium truncate flex-1">{m.name}</span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); moveModel(m.id, "up"); }} disabled={!!search || mi === 0} className={`p-1 disabled:opacity-25 ${modelId === m.id ? "text-white/80 hover:text-white" : "text-ink/40 hover:text-electric"}`} data-testid={`model-up-${m.name}`} title="Flytt opp"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveModel(m.id, "down"); }} disabled={!!search || mi === visible.length - 1} className={`p-1 disabled:opacity-25 ${modelId === m.id ? "text-white/80 hover:text-white" : "text-ink/40 hover:text-electric"}`} data-testid={`model-down-${m.name}`} title="Flytt ned"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); delModel(m.id); }} className={modelId === m.id ? "text-white/80 hover:text-white" : "text-ink/40 hover:text-red-600"} data-testid={`model-delete-${m.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              </React.Fragment>
            ))}
            {models.length === 0 && <p className="text-xs text-ink/40 px-1">Ingen modeller. Legg til under.</p>}
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Merke (valgfritt)" value={newModel.brand} onChange={(e) => setNewModel({ ...newModel, brand: e.target.value })} data-testid="model-brand-input" />
              <input className={inputCls} placeholder="Gruppe/serie (valgfritt)" value={newModel.group} onChange={(e) => setNewModel({ ...newModel, group: e.target.value })} list="model-groups" data-testid="model-group-input" />
              <datalist id="model-groups">{[...new Set(models.map((m) => m.group).filter(Boolean))].map((g) => <option key={g} value={g} />)}</datalist>
            </div>
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Modellnavn" value={newModel.name} onChange={(e) => setNewModel({ ...newModel, name: e.target.value })} data-testid="model-name-input" />
              <button onClick={addModel} disabled={!catId} className="shrink-0 rounded-lg bg-navy px-3 text-white disabled:opacity-40" data-testid="model-add"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {/* Prices */}
        <div className="rounded-xl border border-hairline bg-white p-4 shadow-sm">
          <h3 className="font-heading font-bold text-navy mb-3 flex items-center gap-2"><Tag className="h-4 w-4 text-electric" /> Priser</h3>
          {activeModel ? (
            <>
              <p className="mb-3 text-sm text-ink/60">{activeModel.name}</p>
              <div className="space-y-2">
                {problems.map((p) => (
                  <div key={p} className="flex items-center gap-2" data-testid={`price-row-${p}`}>
                    <span className="flex-1 text-sm text-navy">{p}</span>
                    <input
                      type="number"
                      className="w-28 rounded-lg border border-hairline px-2.5 py-1.5 text-sm text-right focus:border-electric focus:outline-none"
                      placeholder="—"
                      defaultValue={prices[p] ?? ""}
                      key={`${modelId}-${p}-${prices[p]}`}
                      onBlur={(e) => { if (String(e.target.value) !== String(prices[p] ?? "")) savePrice(p, e.target.value); }}
                      data-testid={`price-input-${p}`}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-ink/40">Endres automatisk når du klikker ut av feltet.</p>
            </>
          ) : (
            <p className="text-sm text-ink/50">Velg en modell for å redigere priser.</p>
          )}
        </div>
      </div>
    </div>
  );
}
